/**
 * StockItemsPage - Listagem de Rastreadores, Chips ou Kits (mesma tela, tipo por prop)
 *
 * Um único componente evita três telas paralelas com o mesmo layout; cada tipo
 * tem colunas, filtros e ações próprios e permissão própria (stock_trackers,
 * stock_chips, stock_kits).
 *
 * RELEASE: 2026-10-19 v1.8.0 - Coluna Kit em linha única: selo do código sem quebra ("KIT-" / "000015")
 *                              e cabeçalho da coluna sem quebrar, em Rastreadores e Chips.
 * RELEASE: 2026-10-11 v1.7.0 - Ação Homologar no menu do rastreador (atalho p/ Nova homologação).
 * RELEASE: 2026-10-09 v1.6.0 - Filtros rápidos no estilo pill padrão (QUICK_FILTER_PILL) e busca com lupa.
 * RELEASE: 2026-10-09 v1.5.0 - Alinha colunas à busca (fullBleed) e reduz altura das linhas.
 * RELEASE: 2026-10-09 v1.4.0 - Compacta em cerca de 15% a tipografia das três listagens.
 * RELEASE: 2026-10-08 v1.3.0 - Clique na linha abre ficha com detalhes e movimentações.
 * RELEASE: 2026-10-08 v1.2.0 - Kit somente Ativo/Desmontado; reorganiza colunas e detalhes.
 * RELEASE: 2026-10-06 v1.1.0 - Coluna Local atual, ação Movimentar e exportação (Fases 4/5).
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 3 do Estoque).
 */
import { StockComponentStatus } from '@/components/stock/StockComponentStatus';
import { useEffect, useState } from 'react';
import { ArrowRightLeft, BadgeCheck, Boxes, Cpu, MoreHorizontal, Package, Pencil, Plus, Power, CreditCard, Unlink, Upload, Search } from 'lucide-react';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import {
  ListPageLayout, ListPageHeader, ListPageFiltersBar, ListPageContent,
  LIST_FILTERS_INPUT_CLASS, LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import {
  statusesFor, stockStatusLabel, stockStatusTone, useStockItemMutations, useStockItemsList,
  type StockItemKind, type StockItemRow, type StockItemsFilters,
} from '@/hooks/useStockItems';
import { StockItemFormDialog } from '@/components/stock/StockItemFormDialog';
import { StockHomologationDialog } from '@/components/stock/StockHomologationDialog';
import { dataPlanCarrierLabel, formatPackageMb } from '@/hooks/useStockRegistries';
import { StockDataPlanViewDialog } from '@/components/stock/StockDataPlanViewDialog';
import { StockKitFormDialog } from '@/components/stock/StockKitFormDialog';
import { StockImportDialog } from '@/components/stock/StockImportDialog';
import { StockMoveDialog } from '@/components/stock/StockMoveDialog';
import { StockPlaceLabel } from '@/components/stock/StockPlaceLabel';
import { StockItemDetailsDialog } from '@/components/stock/StockItemDetailsDialog';
import { ExportMenuButton } from '@/components/export/ExportMenuButton';
import type { MovableItem } from '@/hooks/useStockMovements';
import { formatStockDateTime } from '@/lib/stock-presentation';

/** Textos e permissão por tipo. */
const CONFIG: Record<StockItemKind, { title: string; description: string; menuKey: string; newLabel: string; search: string; empty: string; icon: typeof Cpu }> = {
  tracker: { title: 'Rastreadores', description: 'Cada rastreador é controlado individualmente pelo IMEI.', menuKey: 'stock_trackers', newLabel: 'Novo rastreador', search: 'Buscar IMEI ou ESN...', empty: 'Nenhum rastreador encontrado.', icon: Cpu },
  chip: { title: 'Chips', description: 'Cada chip é controlado individualmente pelo ICCID.', menuKey: 'stock_chips', newLabel: 'Novo chip', search: 'Buscar ICCID, linha ou IMSI...', empty: 'Nenhum chip encontrado.', icon: CreditCard },
  kit: { title: 'Kits', description: 'Um rastreador + um chip, movimentados juntos.', menuKey: 'stock_kits', newLabel: 'Montar kit', search: 'Buscar código, IMEI ou ICCID...', empty: 'Nenhum kit encontrado.', icon: Package },
};

/** Selo de situação. */
function StatusBadge({ kind, status }: { kind: StockItemKind; status: string }) {
  return (
    <Badge variant="outline" className="h-5 gap-1.5 whitespace-nowrap px-1.5 text-[9px] font-medium uppercase tracking-wider">
      <StatusDot tone={stockStatusTone(status)} />{stockStatusLabel(kind, status)}
    </Badge>
  );
}

export default function StockItemsPage({ kind }: { kind: StockItemKind }) {
  const cfg = CONFIG[kind];
  const { canCreate, canEdit, isAdmin } = usePermissionsContext();
  // 2026-10-11: atalho Homologar — abre o modal Nova homologação com o rastreador da linha
  const [homologItem, setHomologItem] = useState<StockItemRow | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<StockItemsFilters>({ search: '', status: 'all', kit: 'all', active: 'active', page: 1, pageSize: 25 });
  const [editor, setEditor] = useState<{ open: boolean; item: StockItemRow | null }>({ open: false, item: null });
  const [detailsItem, setDetailsItem] = useState<StockItemRow | null>(null);
  // 2026-10-08: plano aberto em modo visualização pela coluna Plano de dados
  const [viewPlanId, setViewPlanId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [dissolve, setDissolve] = useState<{ kit: StockItemRow | null; reason: string }>({ kit: null, reason: '' });
  const [moveItems, setMoveItems] = useState<MovableItem[] | null>(null);
  const { data, isLoading } = useStockItemsList(kind, filters);
  const { upsertItem, dissolveKit } = useStockItemMutations();

  // Reinicia filtros ao trocar de tela (mesmo componente em rotas diferentes)
  useEffect(() => {
    setSearchInput('');
    setFilters({ search: '', status: 'all', kit: 'all', active: 'active', page: 1, pageSize: 25 });
  }, [kind]);

  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim(), page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const allowCreate = isAdmin || canCreate(cfg.menuKey);
  const allowEdit = isAdmin || canEdit(cfg.menuKey);
  const allowMove = isAdmin || canCreate('stock_movements');
  // 2026-10-11: Homologar segue a mesma permissão do botão "Nova homologação" da tela de Homologação
  const allowHomolog = isAdmin || canCreate('stock_homologations');
  const exportId = { tracker: 'stock_trackers', chip: 'stock_chips', kit: 'stock_kits' }[kind];
  const { page: _pg, pageSize: _ps, ...exportParams } = filters;
  /** Abre a movimentação já com o item escolhido. */
  const openMove = (r: StockItemRow) => setMoveItems([{ type: kind, id: r.id, label: (kind === 'tracker' ? r.imei : kind === 'chip' ? r.iccid : r.code) ?? '', place: r.place ?? null }]);
  const items = data?.items ?? [];

  /** Ativa/inativa rastreador ou chip reenviando o cadastro. */
  const toggleActive = (r: StockItemRow) => {
    if (kind === 'kit') return;
    const base = kind === 'tracker'
      ? { imei: r.imei, esn: r.esn ?? null, supplier_person_id: r.supplier_person_id ?? null, tracker_model_id: r.tracker_model_id, status: r.status, notes: r.notes }
      // 2026-10-07: operadora vem do plano de dados; não reenviamos carrier_name
      : { iccid: r.iccid, line_number: r.line_number, imsi: r.imsi, data_plan_id: r.data_plan_id ?? null, status: r.status, notes: r.notes };
    upsertItem.mutate({ kind, id: r.id, data: { ...base, is_active: !r.is_active } });
  };

  // 2026-10-19: selo em linha única — sem nowrap o código quebraria no hífen ("KIT-" / "000015")
  const KitCell = ({ r }: { r: StockItemRow }) => r.kit_code
    ? <Badge variant="secondary" className="h-5 shrink-0 whitespace-nowrap px-1.5 text-[9px] font-medium">{r.kit_code}</Badge>
    : <span className="whitespace-nowrap text-[10px] text-muted-foreground">Livre</span>;

  return (
    <ListPageLayout>
      <ListPageHeader
        title={cfg.title}
        actions={(
          <div className="flex items-center gap-2">
            <ExportMenuButton sourceId={exportId} params={exportParams} variant="icon" />
            {allowCreate && <>
            {kind !== 'kit' && (
              <Button size="xs" variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="mr-1.5 h-4 w-4" /> Importar
              </Button>
            )}
            <Button size="xs" onClick={() => setEditor({ open: true, item: null })}>
              {kind === 'kit' ? <Boxes className="mr-1.5 h-4 w-4" /> : <Plus className="mr-1.5 h-4 w-4" />} {cfg.newLabel}
            </Button>
            </>}
          </div>
        )}
      />

      <ListPageFiltersBar>
        {/* Busca com lupa, mesmo padrão da listagem de Propostas */}
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground sm:h-3.5 sm:w-3.5" />
          <Input className={`${LIST_FILTERS_INPUT_CLASS} w-full pl-8`} value={searchInput} maxLength={60}
          placeholder={cfg.search} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        {kind !== 'kit' && <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v, page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as situações</SelectItem>
            {statusesFor(kind).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>}
        {kind !== 'kit' && (
          <Select value={filters.kit} onValueChange={(v) => setFilters((f) => ({ ...f, kit: v as StockItemsFilters['kit'], page: 1 }))}>
            <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} shrink-0`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Com ou sem kit</SelectItem>
              <SelectItem value="in_kit">Em kit</SelectItem>
              <SelectItem value="free">Livres</SelectItem>
            </SelectContent>
          </Select>
        )}
        {kind !== 'kit' && <Select value={filters.active} onValueChange={(v) => setFilters((f) => ({ ...f, active: v as StockItemsFilters['active'], page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>}
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        {isLoading ? <Skeleton className="h-40 w-full rounded-lg" /> : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <cfg.icon className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{cfg.empty}</p>
          </div>
        ) : (
          // Mesmo invólucro da listagem de OS: colunas alinhadas à busca; células com py-2.5 (altura como Veículos)
          <Table className={`${kind === 'chip' ? 'min-w-[1200px]' : 'min-w-[820px]'} text-xs [&_td]:py-2.5`}>
              <TableHeader>
                <TableRow>
                  {kind === 'tracker' && <><TableHead className="text-[11px]">IMEI</TableHead><TableHead className="text-[11px]">Modelo</TableHead><TableHead className="text-[11px]">ESN</TableHead><TableHead className="text-[11px]">Fornecedor</TableHead><TableHead className="w-28 min-w-[104px] whitespace-nowrap text-[11px]">Kit</TableHead></>}
                  {kind === 'chip' && <><TableHead className="text-[11px]">ICCID</TableHead><TableHead className="text-[11px]">Linha</TableHead><TableHead className="text-[11px]">Plano de dados</TableHead><TableHead className="w-28 text-[11px]">Pacote (MB)</TableHead><TableHead className="text-[11px]">Fornecedor</TableHead><TableHead className="text-[11px]">Operadoras</TableHead><TableHead className="w-28 min-w-[104px] whitespace-nowrap text-[11px]">Kit</TableHead></>}
                  {kind === 'kit' && <><TableHead className="w-32 text-[11px]">Código</TableHead><TableHead className="w-32 text-[11px]">Situação</TableHead><TableHead className="w-40 text-[11px]">Montado em</TableHead><TableHead className="text-[11px]">Rastreador</TableHead><TableHead className="text-[11px]">Chip</TableHead></>}
                  {kind !== 'kit' && <><TableHead className="w-48 text-[11px]">Local atual</TableHead><TableHead className="w-44 text-[11px]">Situação</TableHead></>}
                  {kind === 'kit' && <TableHead className="w-48 text-[11px]">Local atual</TableHead>}
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetailsItem(r)}>
                    {kind === 'tracker' && <>
                      <TableCell className="font-medium tabular-nums">{r.imei}</TableCell>
                      <TableCell className="text-xs">{r.model_name ? `${r.manufacturer_name ? `${r.manufacturer_name} · ` : ''}${r.model_name}` : '—'}</TableCell>
                      <TableCell className="text-xs tabular-nums">{r.esn || '—'}</TableCell>
                      <TableCell className="text-xs">{r.supplier_name || '—'}</TableCell>
                      <TableCell><KitCell r={r} /></TableCell>
                    </>}
                    {kind === 'chip' && <>
                      <TableCell className="font-medium tabular-nums">{r.iccid}</TableCell>
                       <TableCell className="tabular-nums text-xs">{r.line_number || '—'}</TableCell>
                       <TableCell className="text-xs" onClick={(e) => e.stopPropagation()}>
                        {r.data_plan_id && r.data_plan_name ? (
                          // Nome limitado a 2 linhas para não alongar a listagem; completo no title
                          <button type="button" title={r.data_plan_name} className="line-clamp-2 max-w-[220px] break-words text-left font-medium text-primary hover:underline" onClick={() => setViewPlanId(r.data_plan_id ?? null)}>
                            {r.data_plan_name}
                          </button>
                        ) : '—'}
                      </TableCell>
                       <TableCell className="text-xs tabular-nums">{r.data_plan_id ? formatPackageMb(r.data_plan_package_mb) : '—'}</TableCell>
                       <TableCell className="text-xs">{r.data_plan_supplier_name || '—'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(r.data_plan_carriers?.length ?? 0) > 0
                             ? r.data_plan_carriers?.map((c) => <Badge key={c} variant="secondary" className="text-[9px]">{dataPlanCarrierLabel(c)}</Badge>)
                             : <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                      </TableCell>
                      <TableCell><KitCell r={r} /></TableCell>
                    </>}
                    {kind === 'kit' && <>
                      <TableCell className="font-medium">{r.code}</TableCell>
                      <TableCell><StatusBadge kind="kit" status="active" /></TableCell>
                       <TableCell className="whitespace-nowrap text-xs tabular-nums">{formatStockDateTime(r.formed_at)}</TableCell>
                       <TableCell className="text-xs">
                        <div className="tabular-nums">{r.imei}</div>
                         <div className="truncate text-[10px] text-muted-foreground">{r.model_name || 'Sem modelo'}</div>
                         {/* Situação individual do rastreador (get_stock_items v1.8.0) */}
                         <StockComponentStatus kind="tracker" status={r.tracker_status} />
                      </TableCell>
                       <TableCell className="text-xs">
                        <div className="tabular-nums">{r.iccid}</div>
                         <div className="truncate text-[10px] text-muted-foreground">{r.data_plan_name || 'Sem plano de dados'}</div>
                         {/* Situação individual do chip */}
                         <StockComponentStatus kind="chip" status={r.chip_status} />
                      </TableCell>
                    </>}
                    <TableCell>
                       {/* "Junto com o kit" vai na 2ª linha do local: no máximo 2 linhas */}
                       <StockPlaceLabel place={r.place} compact suffix={r.kit_code && kind !== 'kit' ? 'Junto com o kit' : undefined} />
                    </TableCell>
                    {kind !== 'kit' && <TableCell>
                      <div className="flex items-center gap-1.5">
                        <StatusBadge kind={kind} status={r.status} />
                         {!r.is_active && <Badge variant="outline" className="h-5 px-1.5 text-[9px]">Inativo</Badge>}
                      </div>
                    </TableCell>}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {(allowEdit || allowMove) && (kind !== 'kit' || !r.dissolved_at) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                             <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Ações"><MoreHorizontal className="h-3.5 w-3.5" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* BR-EST-001: item em kit só se move junto com o kit */}
                            {allowMove && r.is_active && !(kind !== 'kit' && r.kit_id) && (
                              <>
                                <DropdownMenuItem onClick={() => openMove(r)}><ArrowRightLeft className="mr-2 h-4 w-4" /> Movimentar</DropdownMenuItem>
                                {allowEdit && <DropdownMenuSeparator />}
                              </>
                            )}
                            {/* 2026-10-11: atalho Homologar (somente rastreadores) abre o modal já com o item */}
                            {kind === 'tracker' && allowHomolog && (
                              <>
                                <DropdownMenuItem onClick={() => setHomologItem(r)}>
                                  <BadgeCheck className="mr-2 h-4 w-4" /> Homologar
                                </DropdownMenuItem>
                                {allowEdit && <DropdownMenuSeparator />}
                              </>
                            )}
                            {!allowEdit ? null : kind === 'kit' ? (
                              <>
                                {/* BR-EST-013: kit instalado em veículo não pode ser desmontado */}
                                <DropdownMenuItem className="text-destructive" disabled={r.place?.type === 'customer'}
                                  onClick={() => setDissolve({ kit: r, reason: '' })}>
                                  <Unlink className="mr-2 h-4 w-4" /> Desmontar kit
                                </DropdownMenuItem>
                                {r.place?.type === 'customer' && (
                                  <p className="px-2 pb-1.5 text-[11px] text-muted-foreground">Retire o kit do veículo antes de desmontar</p>
                                )}
                              </>
                            ) : (
                              <>
                                <DropdownMenuItem onClick={() => setEditor({ open: true, item: r })}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
                                <DropdownMenuItem disabled={!!r.kit_id && r.is_active} onClick={() => toggleActive(r)}>
                                  <Power className="mr-2 h-4 w-4" /> {r.is_active ? 'Inativar' : 'Ativar'}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
          </Table>
        )}
        <PaginationFooter totalItems={data?.total ?? 0} currentPage={filters.page} pageSize={filters.pageSize}
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          onPageSizeChange={(pageSize) => setFilters((f) => ({ ...f, pageSize, page: 1 }))}
          pageSizeOptions={[10, 25, 50, 100]} />
      </ListPageContent>

      {kind === 'kit' ? (
        <StockKitFormDialog open={editor.open} onOpenChange={(open) => setEditor((c) => ({ ...c, open }))} />
      ) : (
        <>
          <StockItemFormDialog kind={kind} open={editor.open} item={editor.item} onOpenChange={(open) => setEditor((c) => ({ ...c, open }))} />
          <StockImportDialog kind={kind} open={importOpen} onOpenChange={setImportOpen} />
        </>
      )}

      <StockMoveDialog open={!!moveItems} initialItems={moveItems ?? undefined} onOpenChange={(o) => !o && setMoveItems(null)} />
      <StockItemDetailsDialog kind={kind} item={detailsItem} onOpenChange={(open) => !open && setDetailsItem(null)} />
      {/* 2026-10-11: Nova homologação a partir da listagem de Rastreadores (somente leitura das regras fica no modal) */}
      <StockHomologationDialog open={!!homologItem} onOpenChange={(o) => !o && setHomologItem(null)} initialTracker={homologItem} />

      {/* Confirmação de desmontagem (motivo opcional fica no histórico do kit) */}
      <Dialog open={!!dissolve.kit} onOpenChange={(o) => !o && setDissolve({ kit: null, reason: '' })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Desmontar {dissolve.kit?.code}?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">O rastreador e o chip ficam livres no mesmo lugar onde o kit estava. O kit continua no histórico.</p>
          <div className="space-y-1.5">
            <Label>Motivo</Label>
            <Textarea value={dissolve.reason} maxLength={500} rows={2} onChange={(e) => setDissolve((d) => ({ ...d, reason: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDissolve({ kit: null, reason: '' })}>Cancelar</Button>
            <Button variant="destructive" disabled={dissolveKit.isPending}
              onClick={() => dissolve.kit && dissolveKit.mutate({ kitId: dissolve.kit.id, reason: dissolve.reason }, { onSuccess: () => setDissolve({ kit: null, reason: '' }) })}>
              Desmontar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <StockDataPlanViewDialog planId={viewPlanId} onOpenChange={(o) => !o && setViewPlanId(null)} />
    </ListPageLayout>
  );
}
